// Blog Service for handling blog operations and OpenAI integration

class BlogService {
  constructor() {
    this.apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    this.baseUrl = 'https://api.openai.com/v1';
  }

  // Generate blog content using OpenAI
  async generateBlogContent(title, topic = '', wordCount = 800) {
    if (!this.apiKey || this.apiKey === 'undefined') {
      throw new Error('OpenAI API key not configured. Please add VITE_OPENAI_API_KEY to your environment variables.');
    }

    const prompt = `Write a professional blog post about "${title}" for a business audience interested in Global Capability Centers (GCCs) and offshore development. 

    ${topic ? `Focus on: ${topic}` : ''}
    
    The blog should:
    - Be approximately ${wordCount} words
    - Include an engaging introduction
    - Have clear sections with headers
    - Provide actionable insights
    - Include industry best practices
    - End with a compelling conclusion
    - Use markdown formatting
    - Be SEO-friendly and engaging
    
    Format the response in markdown with appropriate headers, bullet points, and structure.`;

    try {
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: 'You are a professional content writer specializing in business technology, offshore development, and Global Capability Centers. Write engaging, informative blog posts that provide real value to business executives and technology leaders.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 2000,
          temperature: 0.7
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('Error generating blog content:', error);
      throw error;
    }
  }

  // Generate blog title suggestions
  async generateTitleSuggestions(topic) {
    if (!this.apiKey || this.apiKey === 'undefined') {
      throw new Error('OpenAI API key not configured. Please add VITE_OPENAI_API_KEY to your environment variables.');
    }

    const prompt = `Generate 5 compelling blog post titles about "${topic}" for a business audience interested in Global Capability Centers, offshore development, and business technology. The titles should be:
    - SEO-friendly
    - Engaging and professional
    - Between 50-60 characters
    - Include relevant keywords
    
    Return only the titles, one per line.`;

    try {
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 300,
          temperature: 0.8
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      return data.choices[0].message.content.split('\n').filter(title => title.trim());
    } catch (error) {
      console.error('Error generating title suggestions:', error);
      throw error;
    }
  }

  // Save blog post to local storage (or could be API endpoint)
  saveBlog(blog) {
    try {
      const blogs = this.getBlogs();
      const newBlog = {
        id: Date.now(),
        ...blog,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      blogs.unshift(newBlog);
      localStorage.setItem('admin_blogs', JSON.stringify(blogs));
      return newBlog;
    } catch (error) {
      console.error('Error saving blog:', error);
      throw error;
    }
  }

  // Get all blogs from local storage
  getBlogs() {
    try {
      const blogs = localStorage.getItem('admin_blogs');
      return blogs ? JSON.parse(blogs) : [];
    } catch (error) {
      console.error('Error getting blogs:', error);
      return [];
    }
  }

  // Update blog
  updateBlog(id, updates) {
    try {
      const blogs = this.getBlogs();
      const index = blogs.findIndex(blog => blog.id === id);
      
      if (index === -1) {
        throw new Error('Blog not found');
      }
      
      blogs[index] = {
        ...blogs[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      localStorage.setItem('admin_blogs', JSON.stringify(blogs));
      return blogs[index];
    } catch (error) {
      console.error('Error updating blog:', error);
      throw error;
    }
  }

  // Delete blog
  deleteBlog(id) {
    try {
      const blogs = this.getBlogs();
      const filteredBlogs = blogs.filter(blog => blog.id !== id);
      localStorage.setItem('admin_blogs', JSON.stringify(filteredBlogs));
      return true;
    } catch (error) {
      console.error('Error deleting blog:', error);
      throw error;
    }
  }

  // Publish blog to the resources page
  async publishBlog(id) {
    try {
      const blog = this.getBlogs().find(b => b.id === id);
      if (!blog) {
        throw new Error('Blog not found');
      }

      // Update status to published
      this.updateBlog(id, { status: 'published', publishedAt: new Date().toISOString() });

      // Here you would typically make an API call to update the actual resources page
      // For now, we'll simulate the process
      console.log('Blog published:', blog.title);
      
      return true;
    } catch (error) {
      console.error('Error publishing blog:', error);
      throw error;
    }
  }

  // Get blog statistics
  getBlogStats() {
    const blogs = this.getBlogs();
    return {
      total: blogs.length,
      published: blogs.filter(b => b.status === 'published').length,
      draft: blogs.filter(b => b.status === 'draft').length,
      recent: blogs.filter(b => {
        const blogDate = new Date(b.createdAt);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return blogDate > weekAgo;
      }).length
    };
  }
}

export default new BlogService();
